import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Check, X } from "lucide-react";
import { Button } from "../ui/button";

export type CoursesComparisonTableProps = {
  columns: Array<{ key: string; labelKey: string }>;
  rows: Array<{
    featureKey: string;
    cells: Record<string, string | boolean>;
  }>;
  notes?: Record<string, string>;
  onEnroll?: (courseKey: string) => void;
};

export function CoursesComparisonTable({
  columns,
  rows,
  notes = {},
  onEnroll,
}: CoursesComparisonTableProps) {
  const { t } = useTranslation();

  const renderCellContent = (value: string | boolean, courseKey: string) => {
    if (typeof value === "boolean") {
      return value ? (
        <Check className="w-6 h-6 text-green-500 mx-auto" />
      ) : (
        <X className="w-6 h-6 text-red-500 mx-auto" />
      );
    }

    if (value === "enroll") {
      return (
        <Button
          className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full text-lg font-bold uppercase tracking-wide"
          onClick={() => onEnroll?.(courseKey)}
        >
          {t("breeders.comparison.table.enroll")}
        </Button>
      );
    }

    // Si es una clave de traducción, intentar traducirla
    if (typeof value === "string" && value.startsWith("breeders.")) {
      try {
        const translated = t(value);
        // Si la traducción devuelve la misma clave, significa que no se encontró
        if (translated === value) {
          return <span className="text-sm text-gray-500">{value}</span>;
        }
        return <span className="text-sm">{translated}</span>;
      } catch (error) {
        return <span className="text-sm text-gray-500">{value}</span>;
      }
    }

    return <span className="text-sm">{value}</span>;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      viewport={{ once: true }}
      className="bg-white rounded-3xl shadow-2xl overflow-hidden"
    >
      <div className="w-full">
        <table className="w-full table-fixed">
          <caption className="sr-only">
            {t("breeders.comparison.title1")} {t("breeders.comparison.title2")}
          </caption>
          <thead>
            <tr className="bg-orange-500 text-white">
              <th scope="col" className="px-4 py-4 text-left text-lg font-bold w-1/4">
                {t("breeders.comparison.table.characteristics")}
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-4 py-4 text-center text-lg font-bold w-1/4"
                >
                  {t(column.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={row.featureKey}
                className={`border-b border-gray-200 ${
                  rowIndex % 2 === 1 ? "bg-gray-50" : ""
                }`}
              >
                <td className="px-4 py-4 font-semibold w-1/4">
                  {(() => {
                    try {
                      const translated = t(row.featureKey);
                      // Si la traducción devuelve la misma clave, significa que no se encontró
                      if (translated === row.featureKey) {
                        return <span className="text-gray-500">{row.featureKey}</span>;
                      }
                      return translated;
                    } catch (error) {
                      return <span className="text-gray-500">{row.featureKey}</span>;
                    }
                  })()}
                </td>
                {columns.map((column) => (
                  <td key={column.key} className="px-4 py-4 text-center w-1/4">
                    {renderCellContent(row.cells[column.key], column.key)}
                    {notes[`${row.featureKey}-${column.key}`] && (
                      <div className="text-sm text-gray-600 mt-1">
                        {t(notes[`${row.featureKey}-${column.key}`])}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
